import React from 'react';

import { LUT_GROUPS, css, lutColor, lutLabel } from '../Viewer/lut';
import { REDOX_DEFAULTS, REDOX_FORMULAS as FORMULAS } from '../Viewer/redox';
import { CardTitle, LutRange, Slider } from './ChannelCard';
import { WindowRow } from './BloodChannelCard';

const num = (v, digits = 2) => v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });

// Canal « Redox ratio » (src/components/Viewer/redox.js), présenté comme une modalité : interrupteur (exclusif),
// canaux NAD(P)H et FAD, formule, étalonnage, lissage, fonds, seuil du tissu, plage, LUT, luminosité ; chiffres sur le
// tissu. settings : réglages (null = automatique) ; resolved : valeurs appliquées.
export default function RedoxCard({
  channels, n, f, settings, resolved, stats, active, error, onChange, onWindow, onToggle, collapsed, onCollapse, label,
  onRename,
}) {
  const select = (key, value, aria) => (
    <select value={value?.id || ''} aria-label={aria} onChange={ev => onChange({ [key]: ev.target.value })}>
      {channels.map(ch => <option key={ch.id} value={ch.id}>{ch.name}</option>)}
    </select>
  );
  // Copie 8 bits : les comptes d'origine ne sont connus qu'avec sa fenêtre (voir BloodChannelCard).
  const editableWindow = ch => ch && ch.loader[0].dtype === 'Uint8' && (!ch.toOriginal || !!ch.sourceWindow);
  const unknown = [n, f].filter(ch => ch && !ch.scaleKnown);
  const auto = key => (settings[key] == null ? ' (auto)' : '');
  const counts = key => v => `${Math.round(v)} counts${auto(key)}`;
  const span = settings.formula === 'nadh' ? 4 : 1;  // étendue des curseurs de la plage
  const gradient = `linear-gradient(to right, ${Array.from({ length: 17 }, (_, i) => css(lutColor(settings.lut, [255, 255, 255], false, i / 16, settings.lutRange))).join(', ')})`;
  const r = resolved || {};
  const [bgN, bgF, threshold, lo, hi, bright] = [r.bgN ?? 0, r.bgF ?? 0, r.threshold ?? 0, r.lo ?? 0, r.hi ?? 1, r.bright ?? 1];
  const countMax = Math.max(1000, 2 * (bright || 0));
  return (
    <div className={`channel derived${active ? '' : ' off'}${collapsed ? ' collapsed' : ''}`}>
      <div className="channel-header">
        <span className="swatch redox" title="Computed channel: optical redox ratio" />
        <CardTitle name={label || 'Redox ratio'} collapsed={collapsed} onCollapse={onCollapse} onRename={onRename} />
        <label className="switch" title="Show (hides the other modalities)">
          <input type="checkbox" checked={active} onChange={ev => onToggle(ev.target.checked)} />
          <span />
        </label>
      </div>
      {!collapsed && (
        <div className="channel-body compare">
          <p className="viewer-note" style={{ margin: 0 }}>
            Optical redox ratio = {FORMULAS[settings.formula]}, autofluorescence in camera counts, background removed,
            FAD × calibration. More oxidized: {settings.formula === 'nadh' ? 'lower' : 'higher'} ratio.
          </p>
          <div className="compare-row">
            <span className="compare-label">NAD(P)H</span>{select('n', n, 'NAD(P)H channel (near-UV excitation)')}
          </div>
          <div className="compare-row">
            <span className="compare-label">FAD</span>{select('f', f, 'FAD channel (blue excitation)')}
          </div>
          <div className="compare-row">
            <span className="compare-label">Formula</span>
            <select value={settings.formula} aria-label="Redox ratio formula" onChange={ev => onChange({ formula: ev.target.value })}>
              <option value="fad">{FORMULAS.fad} (0 to 1)</option>
              <option value="nadh">{FORMULAS.nadh}</option>
            </select>
          </div>
          {error && <p className="viewer-note error">{error}</p>}
          {[n, f].filter(editableWindow).map(ch => <WindowRow key={ch.id} channel={ch} onWindow={onWindow} />)}
          {unknown.length > 0 && (
            <p className="viewer-note error" style={{ margin: 0 }}>
              {unknown.map(ch => ch.name).join(', ')}: 8-bit copy without its original window. Enter it (low – high from
              the copy's .window.json file), otherwise the ratio is wrong.
            </p>
          )}
          <Slider label="FAD / NAD(P)H calibration (relative sensitivity of the two channels; 1: raw counts)"
            value={Math.log2(settings.calib)} min={-3} max={3} step={0.01} reset={0} format={v => `FAD × ${num(2 ** v)}`}
            onChange={v => onChange({ calib: Math.round(2 ** v * 1000) / 1000 })} />
          <Slider label="Smoothing before the ratio (σ, full-resolution px)" value={settings.smooth} max={5} step={0.5}
            reset={REDOX_DEFAULTS.smooth} format={v => (v ? `σ ${num(v, 1)} px` : 'none')} onChange={v => onChange({ smooth: v })} />
          <Slider label="NAD(P)H background (no tissue)" value={bgN} max={countMax} step={1} reset={null}
            format={counts('bgN')} onChange={v => onChange({ bgN: v })} />
          <Slider label="FAD background (no tissue)" value={bgF} max={countMax} step={1} reset={null}
            format={counts('bgF')} onChange={v => onChange({ bgF: v })} />
          <Slider label="Tissue threshold (NAD(P)H + FAD)" value={threshold} max={countMax} step={1} reset={null}
            format={counts('threshold')} onChange={v => onChange({ threshold: v })} />
          <div className="compare-row">
            <span className="compare-label">LUT</span>
            <select value={settings.lut} aria-label="Redox ratio LUT" onChange={ev => onChange({ lut: ev.target.value })}>
              {LUT_GROUPS.map(group => (
                <optgroup key={group.label} label={group.label}>
                  {group.ids.map(id => <option key={id} value={id}>{lutLabel(id)}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          <LutRange lut={settings.lut} range={settings.lutRange} onChange={rg => onChange({ lutRange: rg })} />
          <div className="lut-bar" style={{ background: gradient }} />
          <div className="range-values"><span>{num(lo, 3)}</span><span>{num(hi, 3)}</span></div>
          <Slider label={`Range low${auto('lo')}`} value={lo} max={span} step={0.005} reset={null} format={v => num(v, 3)}
            onChange={v => onChange({ lo: v == null ? null : Math.min(v, hi - 0.005) })} />
          <Slider label={`Range high${auto('hi')}`} value={hi} max={span} step={0.005} reset={null} format={v => num(v, 3)}
            onChange={v => onChange({ hi: v == null ? null : Math.max(v, lo + 0.005) })} />
          <div className="channel-options">
            <label className={`chip${settings.weighted ? ' checked' : ''}`}
              title="Brightness from NAD(P)H + FAD (square root, up to the 95th percentile of the tissue): the hue gives the ratio">
              <input type="checkbox" checked={!!settings.weighted} onChange={ev => onChange({ weighted: ev.target.checked })} />
              Brightness = intensity
            </label>
            <button type="button" className="chip"
              onClick={() => onChange({ bgN: null, bgF: null, threshold: null, lo: null, hi: null, bright: null })}
              title="Backgrounds: median without tissue (Otsu); threshold: Otsu of the sum; range: 2nd and 98th percentiles of the ratio on the tissue">
              Automatic values
            </button>
          </div>
          <p className="viewer-note" style={{ margin: 0 }}>
            {stats
              ? `Tissue (${num(stats.area * 100, 1)}% of the image): mean ${num(stats.mean, 3)} · median ${num(stats.median, 3)} · ratio of sums ${num(stats.pooled, 3)}`
              : 'Computing…'}
          </p>
        </div>
      )}
    </div>
  );
}
